/**
 * Builder для создания миграций с fluent API
 */

import { IMigration, MigrationResult, MigrationContext, MigrationType } from '../types/migration-interfaces';
import { ICSElement } from '../types/interfaces';

export class MigrationBuilder {
  private migration: Partial<IMigration> = {
    dependencies: [],
    type: 'custom'
  };

  /**
   * Устанавливает ID миграции
   */
  id(id: string): this {
    this.migration.id = id;
    return this;
  }

  /**
   * Устанавливает версию миграции
   */
  version(version: string): this {
    this.migration.version = version;
    return this;
  }

  /**
   * Устанавливает описание миграции
   */
  description(description: string): this {
    this.migration.description = description;
    return this;
  }

  /**
   * Устанавливает тип миграции
   */
  type(type: MigrationType): this {
    this.migration.type = type;
    return this;
  }

  /**
   * Добавляет зависимость от другой миграции
   */
  dependsOn(migrationId: string): this {
    if (!this.migration.dependencies) {
      this.migration.dependencies = [];
    }
    this.migration.dependencies.push(migrationId);
    return this;
  }

  /**
   * Добавляет несколько зависимостей
   */
  dependsOnMany(migrationIds: string[]): this {
    if (!this.migration.dependencies) {
      this.migration.dependencies = [];
    }
    this.migration.dependencies.push(...migrationIds);
    return this;
  }

  /**
   * Устанавливает функцию применения миграции
   */
  up(upFunction: (context: MigrationContext) => Promise<MigrationResult>): this {
    this.migration.up = upFunction;
    return this;
  }

  /**
   * Устанавливает функцию отката миграции
   */
  down(downFunction: (context: MigrationContext) => Promise<MigrationResult>): this {
    this.migration.down = downFunction;
    return this;
  }

  /**
   * Устанавливает функцию проверки возможности применения
   */
  canApply(canApplyFunction: (context: MigrationContext) => Promise<boolean>): this {
    this.migration.canApply = canApplyFunction;
    return this;
  }

  /**
   * Устанавливает функцию проверки возможности отката
   */
  canRollback(canRollbackFunction: (context: MigrationContext) => Promise<boolean>): this {
    this.migration.canRollback = canRollbackFunction;
    return this;
  }

  /**
   * Устанавливает функцию валидации
   */
  validate(validateFunction: (context: MigrationContext) => Promise<boolean>): this {
    this.migration.validate = validateFunction;
    return this;
  }

  /**
   * Создает миграцию для изменения схемы
   */
  schemaChange(config: {
    schemaName: string;
    oldVersion: string;
    newVersion: string;
    fieldChanges?: {
      added?: string[];
      removed?: string[];
      renamed?: Record<string, string>;
      typeChanged?: Record<string, { from: string; to: string }>;
    };
  }): this {
    this.migration.type = 'schema';
    
    this.migration.up = async (_context: MigrationContext): Promise<MigrationResult> => {
      const startTime = Date.now();
      let processedCount = 0;

      try {
        // Логика изменения схемы
        if (config.fieldChanges?.added) {
          console.log(`Adding fields: ${config.fieldChanges.added.join(', ')}`);
        }
        
        if (config.fieldChanges?.removed) {
          console.log(`Removing fields: ${config.fieldChanges.removed.join(', ')}`);
        }
        
        if (config.fieldChanges?.renamed) {
          console.log(`Renaming fields: ${Object.entries(config.fieldChanges.renamed).map(([old, new_]) => `${old} -> ${new_}`).join(', ')}`);
        }
        
        processedCount = 1; // Одна схема обработана
        
        return {
          success: true,
          processedCount,
          executionTime: Date.now() - startTime,
          message: `Schema ${config.schemaName} updated from ${config.oldVersion} to ${config.newVersion}`
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          executionTime: Date.now() - startTime
        };
      }
    };

    this.migration.down = async (_context: MigrationContext): Promise<MigrationResult> => {
      const startTime = Date.now();
      
      try {
        // Логика отката изменений схемы
        console.log(`Rolling back schema ${config.schemaName} from ${config.newVersion} to ${config.oldVersion}`);
        
        return {
          success: true,
          processedCount: 1,
          executionTime: Date.now() - startTime,
          message: `Schema ${config.schemaName} rolled back to ${config.oldVersion}`
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          executionTime: Date.now() - startTime
        };
      }
    };

    return this;
  }

  /**
   * Создает миграцию для трансформации данных
   */
  dataTransform(config: {
    elementType?: string;
    transformer: (element: ICSElement) => Promise<ICSElement>;
    rollbackTransformer?: (element: ICSElement) => Promise<ICSElement>;
    batchSize?: number;
  }): this {
    this.migration.type = 'data';
    
        this.migration.up = async (_context: MigrationContext): Promise<MigrationResult> => {
      const startTime = Date.now();
      let processedCount = 0;

      try {
        // Здесь была бы логика получения элементов и их трансформации
        // Для демонстрации используем заглушку
        const mockElements: ICSElement[] = []; // Получить элементы из контекста
        
        for (const element of mockElements) {
          await config.transformer(element);
          processedCount++;
        }
        
        return {
          success: true,
          processedCount,
          executionTime: Date.now() - startTime,
          message: `Transformed ${processedCount} elements`
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          processedCount,
          executionTime: Date.now() - startTime
        };
      }
    };

        this.migration.down = async (_context: MigrationContext): Promise<MigrationResult> => {
      const startTime = Date.now();
      let processedCount = 0;

      if (!config.rollbackTransformer) {
        return {
          success: false,
          error: 'No rollback transformer provided',
          executionTime: Date.now() - startTime
        };
      }

      try {
        // Логика отката трансформации данных
        const mockElements: ICSElement[] = []; // Получить элементы из контекста
        
        for (const element of mockElements) {
          await config.rollbackTransformer(element);
          processedCount++;
        }
        
        return {
          success: true,
          processedCount,
          executionTime: Date.now() - startTime,
          message: `Rolled back ${processedCount} elements`
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          processedCount,
          executionTime: Date.now() - startTime
        };
      }
    };

    return this;
  }

  /**
   * Создает миграцию для выполнения SQL-подобных операций
   */
  sqlLike(config: {
    up: string;
    down: string;
    executor?: (query: string) => Promise<any>;
  }): this {
    this.migration.type = 'custom';
    
    this.migration.up = async (_context: MigrationContext): Promise<MigrationResult> => {
      const startTime = Date.now();
      
      try {
        if (config.executor) {
          await config.executor(config.up);
        } else {
          console.log(`Executing UP query: ${config.up}`);
        }
        
        return {
          success: true,
          processedCount: 1,
          executionTime: Date.now() - startTime,
          message: 'SQL-like operation executed successfully'
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          executionTime: Date.now() - startTime
        };
      }
    };

    this.migration.down = async (_context: MigrationContext): Promise<MigrationResult> => {
      const startTime = Date.now();
      
      try {
        if (config.executor) {
          await config.executor(config.down);
        } else {
          console.log(`Executing DOWN query: ${config.down}`);
        }
        
        return {
          success: true,
          processedCount: 1,
          executionTime: Date.now() - startTime,
          message: 'SQL-like rollback executed successfully'
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          executionTime: Date.now() - startTime
        };
      }
    };

    return this;
  }

  /**
   * Создает миграцию с автоматическими проверками
   */
  withAutoChecks(): this {
    this.migration.canApply = async (context: MigrationContext): Promise<boolean> => {
      // Автоматические проверки возможности применения
      return context.direction === 'up';
    };

    this.migration.canRollback = async (context: MigrationContext): Promise<boolean> => {
      // Автоматические проверки возможности отката
      return context.direction === 'down';
    };

    this.migration.validate = async (_context: MigrationContext): Promise<boolean> => {
      // Автоматическая валидация
      return true;
    };

    return this;
  }

  /**
   * Создает финальную миграцию
   */
  build(): IMigration {
    if (!this.migration.id) {
      throw new Error('Migration ID is required');
    }
    
    if (!this.migration.version) {
      throw new Error('Migration version is required');
    }
    
    if (!this.migration.up) {
      throw new Error('Migration up function is required');
    }
    
    if (!this.migration.down) {
      throw new Error('Migration down function is required');
    }

    // Устанавливаем значения по умолчанию
    const migration: IMigration = {
      id: this.migration.id,
      version: this.migration.version,
      description: this.migration.description || `Migration ${this.migration.id}`,
      type: this.migration.type || 'custom',
      dependencies: this.migration.dependencies || [],
      up: this.migration.up,
      down: this.migration.down,
      canApply: this.migration.canApply || (async () => true),
      canRollback: this.migration.canRollback || (async () => true),
      validate: this.migration.validate || (async () => true)
    };

    return migration;
  }
}

/**
 * Создает новый builder для миграции
 */
export function createMigration(): MigrationBuilder {
  return new MigrationBuilder();
} 